/**
 * Normalizes any thrown value (axios error, network failure, etc.)
 * into a single predictable `ApiError`. Consumers (query hooks, UI)
 * should only ever deal with `ApiError`.
 */
import axios from 'axios'
import type { ApiErrorPayload } from './types'

export class ApiError extends Error {
  /** HTTP status code, or 0 for network/unknown errors. */
  readonly status: number
  /** Machine-readable error code from the API, if any. */
  readonly code?: string
  /** Per-field validation messages, if any. */
  readonly fieldErrors?: Record<string, Array<string>>
  /** Original error, kept for debugging. */
  readonly cause?: unknown

  constructor(params: {
    message: string
    status: number
    code?: string
    fieldErrors?: Record<string, Array<string>>
    cause?: unknown
  }) {
    super(params.message)
    this.name = 'ApiError'
    this.status = params.status
    this.code = params.code
    this.fieldErrors = params.fieldErrors
    this.cause = params.cause
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isValidation(): boolean {
    return this.status === 422 || Boolean(this.fieldErrors)
  }

  /**
   * A 5xx. The `message` on one of these is whatever leaked out of the server —
   * on this API often a raw Postgres constraint name — so UI should show its own
   * copy and keep `message` for the console.
   */
  get isServerError(): boolean {
    return this.status >= 500
  }
}

/**
 * `errors` is only usable as per-field messages when it is a plain object.
 *
 * The API also uses that key for a **stack trace string** on a 500. Accepting
 * one as `fieldErrors` made `isValidation` true for a 500 and, worse, every
 * caller doing `Object.entries(fieldErrors)` then iterated the string one
 * CHARACTER at a time and rendered "Q · u · e · r · y · F · a · i…". Anything
 * that is not a plain object is dropped here — it stays reachable on `cause`.
 */
function toFieldErrors(errors: unknown): Record<string, Array<string>> | undefined {
  if (typeof errors !== 'object' || errors === null || Array.isArray(errors)) return undefined
  return Object.keys(errors).length ? (errors as Record<string, Array<string>>) : undefined
}

export function normalizeApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError<ApiErrorPayload>(error)) {
    const status = error.response?.status ?? 0
    const payload = error.response?.data
    return new ApiError({
      message: payload?.message || error.message || 'Request failed',
      status,
      code: payload?.code,
      fieldErrors: toFieldErrors(payload?.errors),
      cause: error,
    })
  }

  if (error instanceof Error) {
    return new ApiError({ message: error.message, status: 0, cause: error })
  }

  return new ApiError({ message: 'Unknown error', status: 0, cause: error })
}
